import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { type ReactNode, useId } from 'react'
import {
  CardItemContent,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { AutoSkeleton } from '@/components/Skeleton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { Radio } from '@/components/inputs/Radio'
import { Form } from '@/components/Form'

interface LegendsDialogProps {
  children?: ReactNode
  isOpen: boolean
  onClose: () => void
  isPending: boolean
  isSubmitDisabled: boolean
  onSubmit: () => unknown
  testId?: string
  submitButtonTestId?: string
  closeButtonTestId?: string
}

/**
 * The LegendsDialog
 */
export function LegendsDialog({
  isOpen,
  isPending,
  isSubmitDisabled,
  children,
  onClose,
  onSubmit,
  testId,
  submitButtonTestId,
  closeButtonTestId,
}: LegendsDialogProps) {
  const formId = useId()
  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>What do you need help with?</ModalDialogTitle>
        <ModalDialogDescription>
          How would you like to file your taxes?
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>
        <Form onSubmit={onSubmit} id={formId}>
          {children}
        </Form>
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Next"
            variant="default"
            isPending={isPending}
            isDisabled={isSubmitDisabled}
            type="submit"
            form={formId}
            testId={submitButtonTestId}
          />
          <Button
            label="Close"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
            testId={closeButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface LegendListItemProps {
  title: string
  description: string
  isSkeleton?: boolean
  isSelected?: boolean
  onSelect?: () => unknown
  isDisabled?: boolean
  testId?: string
}

/**
 * The Legend
 */
export function LegendListItem({
  title,
  description,
  isSkeleton,
  onSelect,
  isSelected,
  isDisabled,
  testId,
}: LegendListItemProps) {
  const { vary } = useSkeletonListContext()
  const titleId = useId()
  return (
    <CardListItem
      textValue={title}
      onAction={onSelect}
      isDisabled={isDisabled}
      testId={testId}
    >
      <CardItemContent
        extras={
          <Radio
            isPadded
            disabled={isDisabled}
            checked={isSelected}
            onChange={onSelect}
            aria-labelledby={titleId}
          />
        }
      >
        <CardTitle id={titleId}>
          <AutoSkeleton isSkeleton={isSkeleton} characters={vary(15)}>
            {title}
          </AutoSkeleton>
        </CardTitle>
        <CardSubtitle variant="label">
          <AutoSkeleton isSkeleton={isSkeleton} characters={vary(80)}>
            {description}
          </AutoSkeleton>
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

export function LegendSkeleton() {
  return (
    <LegendListItem
      title="Legend"
      description="Description"
      isSkeleton
      isDisabled
    />
  )
}

/**
 * No Legends
 */
export function LegendsEmptyState() {
  return (
    <EmptyState>
      <EmptyStateIconDecoration icon="file" />
      <EmptyStateTitle>No legends are available.</EmptyStateTitle>
      <EmptyStateDescription>
        No legend is available for the selected service type.
      </EmptyStateDescription>
    </EmptyState>
  )
}
